import { useEffect, useRef, useState } from 'react';
import { useLoaderData, useLocation, useNavigate } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import type { loader as videosLoader } from '@modules/professional/page/loaders/videos-page-loader.server';
import type { VideoTileProp } from '@modules/professional/type/company-videos';
import { SORT_OPTIONS } from '@modules/professional/component/video-tab/util';
import { NoSearchResults } from '@modules/professional/component/shared/NoSearchResults';
import { DetailTileMedia } from '~/ui-rocco/pages/detail-shared';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import { ProfessionalItemsBar } from './ProfessionalItemsBar';
import { ProfessionalHeadingCtas } from './ProfessionalHeadingCtas';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from '@rocco/components/dialog';
import { useTracker } from '@archipro-website/tracker';
import { cn } from '@rocco/utils/cn';
import { PAGE_TITLE_TYPE, TAB_CONTENT_GAP } from '../utils/tab-spacing';

const VIDEOS_DESCRIPTION =
    'Go behind the work — walkthroughs, project stories and conversations with the team. Press play to see how spaces come together.';

const coverSrcOf = (coverImage: VideoTileProp['coverImage']): string =>
    typeof coverImage === 'string' ? coverImage : (coverImage.src ?? '');

/**
 * Turn a video watch URL ({@link videoTileViewModel} emits `youtu.be/<id>` or
 * `vimeo.com/<id>`) into an autoplaying embed URL. Plain iframes keep the
 * grid dependency-free — react-player's lazy/main entries don't interop
 * cleanly under this Vite build.
 */
const toEmbedUrl = (src: string): string | null => {
    const vimeo = src.match(/vimeo\.com\/(\d+)/);
    if (vimeo) {
        return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`;
    }
    const youtube = src.match(/(?:youtu\.be\/|[?&]v=)([\w-]+)/);
    if (youtube) {
        return `https://www.youtube.com/embed/${youtube[1]}?autoplay=1&rel=0`;
    }
    return null;
};

/**
 * A single editorial video tile: a 16:9 cover with a play affordance that swaps
 * to an autoplaying embed iframe on click (mounted client-side only).
 */
const VideoTileItem = ({
    video,
    onPlay,
}: {
    video: VideoTileProp;
    onPlay: (video: VideoTileProp, trigger: HTMLButtonElement) => void;
}) => {
    const cover = coverSrcOf(video.coverImage);

    return (
        <div className="flex flex-col gap-[0.625rem]">
            <div className="bg-neutral-200 relative aspect-video w-full overflow-hidden rounded-tile">
                <button
                    type="button"
                    onClick={(event) => onPlay(video, event.currentTarget)}
                    aria-label={`Play ${video.caption}`}
                    className="group absolute inset-0 block size-full"
                >
                    <DetailTileMedia
                        src={cover}
                        alt={video.caption}
                        className="size-full"
                    />
                    <span className="absolute inset-0 flex items-center justify-center">
                        <span className="flex items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm transition-colors group-hover:bg-black/70 max-md:size-12 md:size-[4.5rem]">
                            <svg
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                aria-hidden="true"
                                className="max-md:size-5 max-md:translate-x-[1px] md:size-7 md:translate-x-[2px]"
                            >
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        </span>
                    </span>
                </button>
            </div>
            <p className="m-0 font-sans leading-[1.3] font-normal text-[#181818] max-md:text-base md:text-[1.375rem]">
                {video.caption}
            </p>
        </div>
    );
};

const DEFAULT_SORT = 'default';

/**
 * Videos tab (/videos) body — a refine (sort) + search bar over a 3-column grid
 * of 16:9 click-to-play video tiles, matching the other item tabs. Sort
 * navigates the `sortBy` URL the loader reads; search filters by caption
 * client-side. Self-hides when empty; the name / stats / CTA sit in the
 * persistent profile header above.
 */
export const ProfessionalVideosGridSection = () => {
    const { Title } = useProfessionalInitialRouteData();
    const { companyVideosResult } = useLoaderData<typeof videosLoader>();
    const { desktop } = useAppDisplayModeContext();
    const location = useLocation();
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const tracker = useTracker();
    const videos = (companyVideosResult?.videos ?? []) as VideoTileProp[];

    // The open video lives in the URL (?video=<id>) exactly as the legacy tab
    // did, so a specific video stays linkable and Back closes the player
    // instead of leaving the page.
    const openVideoId = new URLSearchParams(location.search).get('video');
    const activeVideo =
        videos.find((v) => String(v.id) === openVideoId) ?? null;
    const triggerRef = useRef<HTMLButtonElement | null>(null);

    const setOpenVideo = (
        video: VideoTileProp | null,
        trigger?: HTMLButtonElement | null,
    ) => {
        if (trigger) triggerRef.current = trigger;
        const params = new URLSearchParams(location.search);
        if (video) params.set('video', String(video.id));
        else params.delete('video');
        const search = params.toString();
        navigate(`${location.pathname}${search ? `?${search}` : ''}`, {
            preventScrollReset: true,
            // CustomScrollRestoration also honours this so opening the player
            // cannot fall through to a top restore when only ?video= changes.
            state: { scroll: false },
        });
    };

    // Logged from an effect rather than the click handler: the click fires
    // before the ?video= param lands, so the logged URL carried no video
    // identity, and a deep-linked open never went through the handler at all.
    //
    // Keyed on the *resolved* video rather than the raw param: a ?video= value
    // matching nothing in this professional's set opens no dialog, and must not
    // log a play. One event per video actually opened, however it opened.
    const activeVideoId = activeVideo?.id;
    useEffect(() => {
        if (!activeVideoId) return;
        tracker.log('Video', { url: new URL(window.location.href) });
    }, [activeVideoId, tracker]);

    if (videos.length === 0) return null;

    const needle = query.trim().toLowerCase();
    const filteredVideos = needle
        ? videos.filter((video) =>
              (video.caption ?? '').toLowerCase().includes(needle),
          )
        : videos;

    const activeSort =
        new URLSearchParams(location.search).get('sortBy') ?? DEFAULT_SORT;
    const sortOptions = SORT_OPTIONS.map((option) => ({
        value: option.param ?? DEFAULT_SORT,
        label: option.item,
    }));
    const onSort = (value: string) => {
        const params = new URLSearchParams(location.search);
        params.set('page', '1');
        if (value && value !== DEFAULT_SORT) params.set('sortBy', value);
        else params.delete('sortBy');
        navigate(`${location.pathname}?${params.toString()}`, {
            preventScrollReset: true,
        });
    };

    return (
        <section className="w-full overflow-hidden bg-white text-[#1f1f21]">
            {/* Desktop keeps the editorial title + intro + CTA cluster; on mobile
                the persistent social-profile header carries the identity/CTAs and
                the tab goes straight to results. */}
            {desktop && (
                <>
                    <div
                        className={cn(
                            'mx-auto w-full max-w-[120rem] md:px-[3.132rem]',
                            TAB_CONTENT_GAP,
                        )}
                    >
                        <div className="flex md:items-start md:justify-between">
                            <h2
                                className={cn(
                                    'm-0 font-sans font-normal text-[#141414]',
                                    PAGE_TITLE_TYPE,
                                )}
                            >
                                Videos
                            </h2>
                            <ProfessionalHeadingCtas />
                        </div>
                        <p className="m-0 font-sans leading-[1.25] font-normal text-[#1f1f21] md:mt-[2.4rem] md:max-w-[60.375rem] md:text-[2.0625rem]">
                            {VIDEOS_DESCRIPTION}
                        </p>
                    </div>
                    <div className="mx-auto w-full max-w-[120rem] md:px-[3.132rem] md:pt-[7.5rem]">
                        <div className="h-px w-full bg-black/20" />
                        <SectionLabel className="mt-[1.062rem] block text-[#181818] md:text-[2rem]">
                            (All Videos)
                        </SectionLabel>
                    </div>
                </>
            )}
            <div className="mx-auto w-full max-w-[120rem] max-md:px-6 max-md:pt-[2.4rem] md:px-[3.132rem] md:pt-[3.75rem]">
                <ProfessionalItemsBar
                    sortOptions={sortOptions}
                    activeSort={activeSort}
                    onSort={onSort}
                    searchValue={query}
                    onSearchChange={setQuery}
                    searchPlaceholder={`Search Video by ${Title}`}
                />
            </div>

            {filteredVideos.length === 0 ? (
                <div className="mx-auto w-full max-w-[120rem] max-md:px-6 max-md:pt-[2.4rem] max-md:pb-14 md:px-[3.132rem] md:pt-[3.6rem] md:pb-[7.5rem]">
                    <NoSearchResults resultsType="videos" />
                </div>
            ) : (
                <div className="mx-auto grid w-full max-w-[120rem] grid-cols-1 max-md:gap-y-8 max-md:px-6 max-md:pt-[2.4rem] md:grid-cols-3 md:gap-x-[1.5rem] md:gap-y-[3.6rem] md:px-[3.132rem] md:pt-[3.6rem]">
                    {filteredVideos.map((video) => (
                        <VideoTileItem
                            key={video.id}
                            video={video}
                            onPlay={(video, trigger) =>
                                setOpenVideo(video, trigger)
                            }
                        />
                    ))}
                </div>
            )}

            {/* Player overlay. DialogContent is not force-mounted, so closing
                unmounts the iframe and the audio stops with it - do not add
                forceMount here. */}
            <Dialog
                open={activeVideo !== null}
                onOpenChange={(open) => {
                    if (!open) setOpenVideo(null);
                }}
            >
                {activeVideo ? (
                    <DialogContent
                        spacing="none"
                        // Keep the page where the reader was. Radix otherwise
                        // focuses the dialog on open, which scrolls the
                        // document to the top. On close, return focus to the
                        // play button without moving the viewport.
                        onOpenAutoFocus={(event) => event.preventDefault()}
                        onCloseAutoFocus={(event) => {
                            event.preventDefault();
                            triggerRef.current?.focus({ preventScroll: true });
                        }}
                        // The default close control is near-black, which all but
                        // disappears on this surface; "rounded" is the light-on-
                        // dark variant. It matters more than usual here: once a
                        // reader clicks into the player, focus belongs to the
                        // cross-origin iframe and Escape no longer reaches the
                        // dialog, so this button is the only way out.
                        closeButtonVariant="rounded"
                        // Width is 92vw to match prod, but also capped against
                        // the viewport height: the player is 16:9, so sizing on
                        // width alone overflowed short-but-wide windows and
                        // carried the close control off-screen with it. With
                        // scroll locked and Escape captured by the iframe, that
                        // left no way out. 7rem covers the top strip plus a
                        // margin below.
                        //
                        // The surface is transparent with that strip of top
                        // padding rather than a black box tight to the video, so
                        // the close control sits above the player instead of
                        // overlapping its chrome.
                        className="w-[min(92vw,calc((100vh-7rem)*16/9))] max-w-none border-0 bg-transparent p-0 pt-14 shadow-none sm:rounded-none"
                        overlayClassName="bg-black/80"
                    >
                        <DialogTitle className="sr-only">
                            {activeVideo.caption}
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            Video player
                        </DialogDescription>
                        {toEmbedUrl(activeVideo.src) ? (
                            <iframe
                                src={toEmbedUrl(activeVideo.src) ?? undefined}
                                title={activeVideo.caption}
                                className="aspect-video w-full rounded-[0.5rem] bg-black"
                                allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                                allowFullScreen
                                tabIndex={-1}
                            />
                        ) : null}
                    </DialogContent>
                ) : null}
            </Dialog>
        </section>
    );
};
